import React, { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Upload, Download, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";

/**
 * Generic CSV bulk-import dialog.
 * Props:
 *   open, onOpenChange, title
 *   endpoint — e.g. "/panels/bulk-import" or "/devices/bulk-import"
 *   templateRows — array of CSV lines that make up the downloadable template
 *   onDone — callback after successful import (to refresh table)
 *   templateFilename
 */
export default function CsvImportDialog({
  open, onOpenChange, title, endpoint, templateRows, templateFilename, onDone,
}) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const reset = () => { setFile(null); setResult(null); setBusy(false); };

  const onClose = (o) => {
    onOpenChange(o);
    if (!o) { reset(); onDone && result?.created > 0 && onDone(); }
  };

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv") && f.type !== "text/csv") {
      toast.error("Please upload a .csv file");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      const token = localStorage.getItem("f360_token");
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "text/csv",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: text,
      });
      if (!res.ok) {
        const err = await res.text();
        toast.error(`Upload failed: ${res.status} ${err.slice(0, 100)}`);
        setBusy(false);
        return;
      }
      const data = await res.json();
      setResult(data);
      if (data.created > 0) toast.success(`Imported ${data.created} rows`);
      if (data.errors?.length) toast.warning(`${data.errors.length} rows had errors`);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([templateRows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = templateFilename || "template.csv";
    a.click();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-white/10 text-white max-w-lg">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Button variant="outline" size="sm" onClick={downloadTemplate} className="border-white/15 text-slate-200" data-testid="csv-template-btn">
            <Download className="w-4 h-4 mr-1" strokeWidth={2.5} /> Download CSV template
          </Button>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
            onClick={() => inputRef.current?.click()}
            className={`rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition ${dragging ? "border-orange-500 bg-orange-500/5" : "border-white/10 bg-white/5 hover:border-orange-500/40"}`}
            data-testid="csv-dropzone"
          >
            <Upload className="w-8 h-8 mx-auto mb-2 text-orange-400" strokeWidth={2.5} />
            {file ? (
              <div className="text-sm font-semibold">{file.name} <span className="text-xs text-slate-400">({(file.size / 1024).toFixed(1)} kB)</span></div>
            ) : (
              <>
                <div className="text-sm font-semibold">Drop a .csv file or click to browse</div>
                <div className="text-xs text-slate-400 mt-1">UTF-8 encoded · first row must be the header</div>
              </>
            )}
            <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="csv-file-input" />
          </div>

          {result && (
            <div className="space-y-2">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-sm flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" strokeWidth={2.5} />
                <span>Created <b>{result.created}</b> of {result.total_rows} rows</span>
              </div>
              {result.errors?.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-xs max-h-40 overflow-y-auto">
                  <div className="flex items-center gap-2 text-red-300 font-bold mb-2"><AlertCircle className="w-4 h-4" strokeWidth={2.5} /> {result.errors.length} errors</div>
                  <ul className="space-y-1 text-red-200">
                    {result.errors.slice(0, 20).map((e, i) => (
                      <li key={i}>Row {e.row}: {e.error}</li>
                    ))}
                    {result.errors.length > 20 && <li className="text-slate-400">…and {result.errors.length - 20} more</li>}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose(false)}>{result?.created ? "Done" : "Cancel"}</Button>
          {!result && <Button disabled={!file || busy} onClick={upload} className="f360-gradient border-0" data-testid="csv-import-btn">{busy ? "Importing…" : "Import"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
